import { useAuthStore } from '../store/useAuthStore';
import { useEffect } from 'react';
import { api } from '../api/client';

// Permission modules
export type PermissionModule =
  | 'articles'
  | 'orders'
  | 'offers'
  | 'inventory'
  | 'shipping'
  | 'picking'
  | 'customers'
  | 'invoices'
  | 'delivery_notes'
  | 'accounting'
  | 'purchasing'
  | 'reports'
  | 'performance'
  | 'settings'
  | 'users'
  | 'integrations'
  // V-2788: Geräte-Einstellungen der Kassen (JTL-Seite) — das Backend liefert das Modul schon.
  | 'pos_sync'
  // Welle 1 Paket 1 (V-0086): fehlte hier komplett, obwohl das Backend das Modul längst kennt
  // (Stripe des Mandanten, PayPal, Zahlungsabgleich, SumUp-Verknüpfung) — die Oberfläche konnte
  // es damit nie schalten, nur der serverseitige Rückfall auf die Rolle griff.
  | 'banking'
  | 'gambio'
  | 'ffn'
  | 'returns'
  | 'workshop_orders'
  | 'repairs'
  | 'documents'
  | 'printers'
  | 'pack_scope'
  | 'cost_visibility'
  | 'price_override'
  | 'article_pricing'
  | 'discount_below_cost'
  // V-1050: Treuepunkte von Hand ändern — Fähigkeits-Recht, ab Werk nur Administrator und Manager.
  | 'loyalty_adjust'
  // V-1585: Betriebszahlen im Dashboard (Umsatz, Aufträge, Forderungen, Versand) — Fähigkeits-Recht,
  // ab Werk Administrator, Manager, Buchhalter. Serverseitig durchgesetzt (`Betriebszahlensicht`).
  | 'dashboard_figures'
  // V-1463/V-1547 (10.10.2026): Gutscheine und Treueprogramm — eigene Module (Sehen/Ändern getrennt).
  | 'vouchers'
  | 'loyalty'
  // V-3640: Ausweiskopien, Ausweisnummer und Geburtsdatum ansehen/entfernen — Fähigkeits-Recht, ab Werk nur Administrator.
  | 'ankauf_ausweis';

// Permission operations
export type PermissionOperation = 'view' | 'create' | 'edit' | 'delete' | 'export' | 'sync';

// Module metadata
export const MODULES: Record<PermissionModule, { name: string; description: string }> = {
  articles: { name: 'Artikel', description: 'Artikelstammdaten, Varianten' },
  orders: { name: 'Aufträge', description: 'Order Desk, Auftragsverwaltung' },
  offers: { name: 'Angebote', description: 'Angebote erstellen, versenden und in Aufträge umwandeln' },
  inventory: { name: 'Lager', description: 'Bestand, Lagerplätze, MHD' },
  shipping: { name: 'Versand', description: 'Paketlabels, Sendungsverfolgung' },
  picking: { name: 'Kommissionierung', description: 'Picking, Packing' },
  customers: { name: 'Kunden', description: 'CRM, Kundenverwaltung' },
  invoices: { name: 'Rechnungen', description: 'Rechnungserstellung' },
  delivery_notes: { name: 'Lieferscheine', description: 'Lieferschein-Erstellung, Druck, E-Mail-Versand' },
  accounting: { name: 'Buchhaltung', description: 'Steuern, DATEV' },
  purchasing: { name: 'Einkauf', description: 'Einkauf, Bestellvorschläge, Lieferantenpreise' },
  reports: { name: 'Berichte', description: 'Dashboard, Statistiken' },
  // 🚨 Bis 20.09.2026 kannte das Backend dieses Modul (PermissionService::MODULES),
  // die Oberflaeche aber nicht — die Rechtematrix konnte es damit nicht vergeben.
  performance: { name: 'Mitarbeiter-Leistung', description: 'Pick & Pack und Vertriebs-Scorecard' },
  settings: { name: 'Einstellungen', description: 'Systemkonfiguration' },
  users: { name: 'Benutzer', description: 'Benutzerverwaltung' },
  integrations: { name: 'Schnittstellen', description: 'Marktplätze, Shops' },
  banking: { name: 'Banking', description: 'Bankverbindungen, Kontoumsätze, Zahlungsabgleich, Überweisungen' },
  gambio: { name: 'Gambio GX4', description: 'Gambio Multi-Shop und Sync' },
  ffn: { name: 'Fulfillment-Network', description: 'JTL-Fulfillment-Network: Aufträge an den Dienstleister, Bestand, Retouren' },
  returns: { name: 'Retouren', description: 'Retourenmanagement, RMA, Zustandsbewertung' },
  workshop_orders: { name: 'Werkstattaufträge (Hersteller)', description: 'Eigene Ware zum Hersteller/Dienstleister im Retourenfall' },
  repairs: { name: 'Reparaturaufträge', description: 'Kundengeräte in der eigenen Werkstatt: Status, Termin, Online-Verfolgung' },
  documents: { name: 'Dokumentvorlagen', description: 'Vorlagen für Rechnungen, Lieferscheine, Auftragsbestätigungen' },
  printers: { name: 'Druckerverwaltung', description: 'BISPrint-Clients, Arbeitsplätze, Drucker-Zuweisung, Direktdruck-Einstellungen' },
  // 🚨 Diese Liste ist eine EIGENE, kürzere Kopie der Backend-Module (PermissionService.php).
  // Wer sie beim Anlegen eines Rechts vergisst, bekommt eines, das der Server kennt und die
  // Oberfläche nicht — die Rechtematrix zeigt es dann nirgends an.
  pack_scope: { name: 'Packtisch-Vorgabe', description: 'Eigene Versandarten und Wagen-/Packtischnamen am Lagergerät ändern' },
  cost_visibility: { name: 'Einkaufspreise & Gewinn', description: 'Einkaufspreise, Marge, Deckungsbeitrag und Lagerwert sehen — in Dashboard, Artikeln, Berichten, Export und BISCalc-Preiskalkulation' },
  price_override: { name: 'Preise im Auftrag ändern', description: 'Im Auftrag einen vom Katalog abweichenden Preis setzen — ohne dieses Recht ist der Preis gebunden oder freigabepflichtig' },
  article_pricing: { name: 'Artikelpreise ändern', description: 'Verkaufs- und Einkaufspreise am Artikelstamm ändern — ohne dieses Recht bleiben die Preisfelder beim Speichern unverändert' },
  discount_below_cost: { name: 'Rabatt unter Einkaufspreis', description: 'Einen Auftrag freigeben, dessen Preise den Einkaufspreis unterschreiten — ohne dieses Recht wartet der Vorgang auf Zustimmung' },
  loyalty_adjust: { name: 'Treuepunkte korrigieren', description: 'Den Punktestand eines Treuekontos von Hand ändern (Gutschrift oder Abzug mit Begründung) — ab Werk nur Administrator und Manager' },
  dashboard_figures: { name: 'Betriebszahlen im Dashboard', description: 'Umsatz, Aufträge, offene Forderungen und Versandzahlen im Dashboard sehen — ohne dieses Recht bleiben nur die Arbeitskacheln' },
  vouchers: { name: 'Gutscheine', description: 'Gutscheine und Gutscheinvorlagen ansehen bzw. anlegen, ändern, importieren und löschen — ab Werk Administrator und Manager (Buchhalter nur ansehen)' },
  loyalty: { name: 'Treueprogramm', description: 'Treueprogramm, Stufen, Konten und Ereignisse ansehen bzw. Programm, Stufen und Ausschlüsse ändern und Konten löschen — ab Werk Administrator und Manager (Buchhalter und Vertrieb nur ansehen)' },
  ankauf_ausweis: { name: 'Ausweisdaten ansehen', description: 'Ausweiskopien sowie Ausweisnummer und Geburtsdatum von Verkäufern (Geräte-Ankauf) und Käufern ansehen bzw. entfernen — ab Werk nur Administrator' },
  pos_sync: { name: 'POS-Synchronisation', description: 'Datenabgleich zwischen WaWi und POS-Kassensystem' },
};

// Operation labels
export const OPERATIONS: Record<PermissionOperation, string> = {
  view: 'Anzeigen',
  create: 'Erstellen',
  edit: 'Bearbeiten',
  delete: 'Löschen',
  export: 'Exportieren',
  sync: 'Synchronisieren',
};

// User roles
export type UserRole = 'admin' | 'manager' | 'warehouse' | 'shipping' | 'readonly' | 'accountant' | 'sales';

// Role labels
export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrator',
  manager: 'Manager',
  warehouse: 'Lagermitarbeiter',
  shipping: 'Versandmitarbeiter',
  readonly: 'Nur Lesen',
  accountant: 'Buchhalter',
  sales: 'Vertrieb',
};

// Default permissions for roles
const DEFAULT_ROLE_PERMISSIONS: Record<UserRole, Record<PermissionModule, PermissionOperation[]>> = {
  admin: {
    documents: ['view', 'create', 'edit', 'delete'],
    articles: ['view', 'create', 'edit', 'delete', 'export'],
    orders: ['view', 'create', 'edit', 'delete', 'export'],
    offers: ['view', 'create', 'edit', 'delete', 'export'],
    inventory: ['view', 'create', 'edit', 'delete', 'export'],
    shipping: ['view', 'create', 'edit', 'delete', 'export'],
    picking: ['view', 'create', 'edit', 'delete', 'export'],
    customers: ['view', 'create', 'edit', 'delete', 'export'],
    invoices: ['view', 'create', 'edit', 'delete', 'export'],
    delivery_notes: ['view', 'create', 'edit', 'delete', 'export'],
    accounting: ['view', 'create', 'edit', 'delete', 'export'],
    purchasing: ['view', 'create', 'edit', 'delete', 'export'],
    reports: ['view', 'create', 'edit', 'delete', 'export'],
    performance: ['view', 'create', 'edit', 'delete', 'export'],
    settings: ['view', 'create', 'edit', 'delete', 'export'],
    users: ['view', 'create', 'edit', 'delete', 'export'],
    integrations: ['view', 'create', 'edit', 'delete', 'export', 'sync'],
    // Welle 1 Paket 1 (V-0086): Werksvorgabe angeglichen an RoleService::getDefaultPermissionsForCode().
    banking: ['view', 'create', 'edit', 'delete', 'sync'],
    gambio: ['view', 'create', 'edit', 'delete', 'export', 'sync'],
    ffn: ['view', 'create', 'edit', 'delete', 'export', 'sync'],
    // Retouren/Werkstatt/Reparaturen: bis 18.08.2026 fehlten diese drei Module hier,
    // obwohl das Backend sie kennt — die Rechtematrix konnte sie damit nicht vergeben.
    returns: ['view', 'create', 'edit', 'delete', 'export'],
    workshop_orders: ['view', 'create', 'edit', 'delete', 'export'],
    repairs: ['view', 'create', 'edit', 'delete', 'export'],
    printers: ['view', 'create', 'edit', 'delete'],
    // Packtisch-Vorgabe am Gerät ändern: Leitung ja, einfache Packer nein (Ben-Entscheid
    // 14.08.2026) — sonst stellt sich jeder seine eigene Warteschlange um.
    pack_scope: ['view', 'edit'],
    cost_visibility: ['view'],
    price_override: ['edit'],
    article_pricing: ['edit'],
    discount_below_cost: ['edit'],
    loyalty_adjust: ['edit'],
    dashboard_figures: ['view'],
    vouchers: ['view', 'edit'],
    loyalty: ['view', 'edit'],
    pos_sync: ['view', 'create', 'edit', 'delete'],
    ankauf_ausweis: ['view', 'edit'],
  },
  manager: {
    documents: ['view', 'create', 'edit', 'delete'],
    articles: ['view', 'create', 'edit', 'delete', 'export'],
    orders: ['view', 'create', 'edit', 'delete', 'export'],
    offers: ['view', 'create', 'edit', 'delete', 'export'],
    inventory: ['view', 'create', 'edit', 'delete', 'export'],
    shipping: ['view', 'create', 'edit', 'delete', 'export'],
    picking: ['view', 'create', 'edit', 'delete', 'export'],
    customers: ['view', 'create', 'edit', 'delete', 'export'],
    invoices: ['view', 'create', 'edit', 'delete', 'export'],
    delivery_notes: ['view', 'create', 'edit', 'delete', 'export'],
    // Welle 1 Paket 1 (V-0086, Ben): der Manager darf Eingangsrechnungen freigeben — `edit` ergänzt.
    accounting: ['view', 'edit'],
    purchasing: ['view', 'create', 'edit', 'delete', 'export'],
    reports: ['view', 'create', 'edit', 'delete', 'export'],
    performance: ['view', 'create', 'edit', 'delete', 'export'],
    settings: ['view', 'edit'],
    users: ['view'],
    integrations: ['view'],
    // Welle 1 Paket 1 (V-0086): `sync` ergänzt (Stripe-/PayPal-Abgleich).
    banking: ['view', 'edit', 'sync'],
    gambio: ['view', 'create', 'edit', 'delete', 'export', 'sync'],
    ffn: ['view', 'create', 'edit', 'export', 'sync'],
    returns: ['view', 'create', 'edit', 'delete', 'export'],
    workshop_orders: ['view', 'create', 'edit', 'delete', 'export'],
    repairs: ['view', 'create', 'edit', 'delete', 'export'],
    printers: ['view'],
    pack_scope: ['view', 'edit'],
    cost_visibility: ['view'],
    price_override: ['edit'],
    article_pricing: ['edit'],
    discount_below_cost: ['edit'],
    loyalty_adjust: ['edit'],
    dashboard_figures: ['view'],
    vouchers: ['view', 'edit'],
    loyalty: ['view', 'edit'],
    pos_sync: ['view', 'create', 'edit', 'delete'],
    ankauf_ausweis: [],
  },
  warehouse: {
    documents: ['view'],
    articles: ['view', 'export'],
    orders: ['view', 'edit'],
    offers: [],
    inventory: ['view', 'create', 'edit', 'delete', 'export'],
    shipping: ['view', 'edit'],
    picking: ['view', 'create', 'edit', 'delete', 'export'],
    customers: ['view'],
    invoices: ['view'],
    delivery_notes: ['view', 'create', 'export'],
    accounting: [],
    purchasing: ['view', 'export'],
    reports: ['view'],
    performance: ['view'],
    settings: ['view'],
    users: [],
    integrations: [],
    banking: [],
    gambio: [],
    ffn: ['view'],
    returns: ['view', 'create', 'edit'],
    workshop_orders: ['view', 'edit'],
    repairs: ['view'],
    printers: ['view'],
    // Nur ansehen: Der Packer arbeitet nach der Vorgabe, er stellt sie nicht.
    pack_scope: ['view'],
    cost_visibility: [],
    price_override: [],
    article_pricing: [],
    discount_below_cost: [],
    loyalty_adjust: [],
    dashboard_figures: [],
    vouchers: [],
    loyalty: [],
    pos_sync: ['view'],
    ankauf_ausweis: [],
  },
  shipping: {
    documents: ['view'],
    articles: ['view'],
    orders: ['view', 'edit'],
    offers: [],
    inventory: ['view'],
    shipping: ['view', 'create', 'edit', 'delete', 'export'],
    picking: ['view', 'edit'],
    customers: ['view'],
    invoices: ['view'],
    delivery_notes: ['view', 'create', 'edit', 'export'],
    accounting: [],
    purchasing: ['view', 'create', 'edit', 'delete', 'export'],
    reports: ['view'],
    performance: ['view'],
    settings: ['view'],
    users: [],
    integrations: [],
    banking: [],
    gambio: [],
    ffn: ['view'],
    returns: ['view', 'edit'],
    workshop_orders: ['view'],
    repairs: ['view'],
    printers: ['view'],
    pack_scope: ['view'],
    cost_visibility: [],
    price_override: [],
    article_pricing: [],
    discount_below_cost: [],
    loyalty_adjust: [],
    dashboard_figures: [],
    vouchers: [],
    loyalty: [],
    pos_sync: [],
    ankauf_ausweis: [],
  },
  readonly: {
    documents: ['view'],
    articles: ['view'],
    orders: ['view'],
    offers: ['view'],
    inventory: ['view'],
    shipping: ['view'],
    picking: ['view'],
    customers: ['view'],
    invoices: ['view'],
    delivery_notes: ['view'],
    accounting: [],
    purchasing: ['view'],
    reports: ['view'],
    performance: ['view'],
    settings: ['view'],
    users: [],
    integrations: [],
    banking: [],
    gambio: ['view'],
    ffn: ['view'],
    returns: ['view'],
    workshop_orders: ['view'],
    repairs: ['view'],
    printers: ['view'],
    pack_scope: ['view'],
    cost_visibility: [],
    price_override: [],
    article_pricing: [],
    discount_below_cost: [],
    loyalty_adjust: [],
    dashboard_figures: [],
    vouchers: [],
    loyalty: [],
    pos_sync: ['view'],
    ankauf_ausweis: [],
  },
  accountant: {
    documents: ['view'],
    articles: ['view', 'export'],
    orders: ['view', 'export'],
    offers: ['view', 'export'],
    inventory: ['view', 'export'],
    shipping: ['view'],
    picking: ['view'],
    customers: ['view', 'export'],
    invoices: ['view', 'create', 'edit', 'delete', 'export'],
    delivery_notes: ['view', 'export'],
    accounting: ['view', 'create', 'edit', 'delete', 'export'],
    purchasing: ['view', 'create', 'edit', 'export'],
    reports: ['view', 'create', 'edit', 'export'],
    performance: [],
    settings: ['view'],
    users: [],
    integrations: [],
    banking: ['view', 'create', 'edit', 'sync'],
    gambio: ['view'],
    ffn: ['view'],
    returns: ['view', 'export'],
    workshop_orders: ['view'],
    repairs: ['view'],
    printers: ['view'],
    pack_scope: ['view'],
    cost_visibility: ['view'],
    price_override: [],
    article_pricing: [],
    discount_below_cost: [],
    loyalty_adjust: [],
    dashboard_figures: ['view'],
    vouchers: ['view'],
    loyalty: ['view'],
    pos_sync: ['view'],
    ankauf_ausweis: [],
  },
  sales: {
    documents: ['view'],
    articles: ['view', 'export'],
    orders: ['view', 'create', 'edit', 'delete', 'export'],
    offers: ['view', 'create', 'edit', 'delete', 'export'],
    inventory: ['view'],
    shipping: ['view'],
    picking: ['view'],
    customers: ['view', 'create', 'edit', 'delete', 'export'],
    invoices: ['view', 'create', 'edit', 'export'],
    delivery_notes: ['view', 'create', 'export'],
    accounting: [],
    purchasing: [],
    // V-1585 (06.10.2026): Berichte gehören nicht mehr zur Werksrolle Vertrieb (wie im Backend).
    reports: [],
    performance: ['view'],
    settings: ['view'],
    users: [],
    integrations: [],
    banking: [],
    gambio: ['view'],
    ffn: ['view'],
    returns: ['view', 'create', 'edit'],
    workshop_orders: ['view'],
    repairs: ['view', 'edit'],
    printers: ['view'],
    pack_scope: ['view'],
    cost_visibility: [],
    price_override: [],
    article_pricing: [],
    discount_below_cost: [],
    loyalty_adjust: [],
    dashboard_figures: [],
    vouchers: [],
    loyalty: ['view'],
    pos_sync: ['view'],
    ankauf_ausweis: [],
  },
};

/**
 * Die EINE Rechteregel der Oberfläche (Admin → gespeicherte Rechte → Rollen-Vorgabe). Der Hook und Stores
 * (V-1585 F-010: `useInvoiceStore` ohne Hook) rufen dieselbe Funktion.
 */
function pruefeRecht(
  user: { role?: string } | null | undefined,
  permissions: Record<string, Record<string, boolean>> | null | undefined,
  module: PermissionModule,
  operation: PermissionOperation,
): boolean {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (permissions && permissions[module]) {
    return permissions[module][operation] === true;
  }
  const rolePermissions = DEFAULT_ROLE_PERMISSIONS[user.role as UserRole];
  if (!rolePermissions) return false;
  const modulePermissions = rolePermissions[module];
  if (!modulePermissions) return false;
  return modulePermissions.includes(operation);
}

/** Recht prüfen außerhalb von React-Komponenten (Stores). */
export function darfAusserhalbVonReact(module: PermissionModule, operation: PermissionOperation): boolean {
  const { user, permissions } = useAuthStore.getState();
  return pruefeRecht(user, permissions as never, module, operation);
}

/**
 * Merker, für welchen Benutzer die Rechte bereits geholt wurden.
 *
 * 🚨 **Modulweit, nicht je Komponente.** `usePermission()` steckt in Dutzenden Komponenten; ein
 * Zustand innerhalb des Hooks hiesse eine Anfrage pro Aufrufer. Der Schlüssel ist die
 * Benutzerkennung, damit ein Kontowechsel die Rechte neu holt statt die des Vorgängers
 * weiterzuverwenden.
 */
let rechteGeholtFuer: string | null = null;

/**
 * Hook to check permissions for the current user
 */
export function usePermission() {
  const { user, permissions, setPermissions, blockedWidgets, setBlockedWidgets } = useAuthStore();

  /**
   * Die tatsächlich vergebenen Rechte einmalig nachladen.
   *
   * 🚨 **Ohne das prüfte die Oberfläche gegen `DEFAULT_ROLE_PERMISSIONS`** — eine fest
   * einprogrammierte Liste. `setPermissions()` wurde nirgends aufgerufen, `permissions` blieb
   * `null`. Solange die Werksvorgabe galt, stimmten beide Listen überein; wer aber einem
   * Benutzer ein Recht individuell gab, erlebte den Bruch: Der Server lieferte die Daten, die
   * Oberfläche blendete sie trotzdem aus.
   *
   * Schlägt der Abruf fehl, bleibt es beim bisherigen Verhalten (Rollen-Vorgabe). Das ist der
   * sichere Rückfall — ein leeres Rechtepaket würde dem Benutzer die halbe Anwendung ausblenden.
   */
  useEffect(() => {
    if (!user?.id || rechteGeholtFuer === user.id) {
      return;
    }
    rechteGeholtFuer = user.id;

    let abgebrochen = false;
    (async () => {
      try {
        const antwort = await api.get<{
          permissions?: Record<string, Record<string, boolean>>;
          blockedWidgets?: string[];
        }>('/permissions/me');
        if (!abgebrochen && antwort?.permissions && Object.keys(antwort.permissions).length > 0) {
          setPermissions(antwort.permissions as never);
          // Kommt im selben Zug — sonst rendert das Dashboard einen Moment lang mit Rechten,
          // aber ohne Sperre, und eine abgeschaltete Kachel blitzt auf.
          setBlockedWidgets(antwort.blockedWidgets ?? []);
        }
      } catch {
        // Rollen-Vorgabe bleibt gültig.
        rechteGeholtFuer = null;
      }
    })();

    return () => { abgebrochen = true; };
  }, [user?.id, setPermissions, setBlockedWidgets]);

  /**
   * Check if user has permission for a specific module and operation
   */
  const hasPermission = (module: PermissionModule, operation: PermissionOperation): boolean =>
    pruefeRecht(user, permissions, module, operation);

  /**
   * Check if user can view a module
   */
  const canView = (module: PermissionModule): boolean => hasPermission(module, 'view');

  /**
   * Check if user can create in a module
   */
  const canCreate = (module: PermissionModule): boolean => hasPermission(module, 'create');

  /**
   * Check if user can edit in a module
   */
  const canEdit = (module: PermissionModule): boolean => hasPermission(module, 'edit');

  /**
   * Check if user can delete in a module
   */
  const canDelete = (module: PermissionModule): boolean => hasPermission(module, 'delete');

  /**
   * Check if user can export from a module
   */
  const canExport = (module: PermissionModule): boolean => hasPermission(module, 'export');

  const canSync = (module: PermissionModule): boolean => hasPermission(module, 'sync');

  /**
   * Get all permissions for a module
   */
  const getModulePermissions = (module: PermissionModule): Record<PermissionOperation, boolean> => {
    return {
      view: hasPermission(module, 'view'),
      create: hasPermission(module, 'create'),
      edit: hasPermission(module, 'edit'),
      delete: hasPermission(module, 'delete'),
      export: hasPermission(module, 'export'),
      sync: hasPermission(module, 'sync'),
    };
  };

  /**
   * Check if user is admin
   */
  const isAdmin = (): boolean => user?.role === 'admin';

  /**
   * Get user's role
   */
  const getRole = (): UserRole | null => (user?.role as UserRole) ?? null;

  /**
   * Ist diese Dashboard-Kachel für die Rolle des Benutzers abgeschaltet?
   *
   * 🚨 **Etwas anderes als ein fehlendes Recht.** Das Recht sagt, ob jemand eine Zahl sehen
   * DARF; die Sperre sagt, ob sie auf SEIN Dashboard gehört. Ein Vertriebsmitarbeiter darf den
   * Umsatz kennen — die Lagerleitung kann trotzdem entscheiden, dass er im Lager-Dashboard
   * nichts zu suchen hat. Deshalb zwei getrennte Prüfungen, nicht eine vermischte.
   */
  const istWidgetGesperrt = (widgetId: string): boolean => {
    if (!user) return false;
    // Administratoren umgehen jede Rechteprüfung ohnehin — eine Kachel vor ihnen zu verbergen,
    // wäre eine Sperre, die sie mit zwei Klicks wieder aufheben.
    if (user.role === 'admin') return false;

    return blockedWidgets.includes(widgetId);
  };

  return {
    istWidgetGesperrt,
    blockedWidgets,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canExport,
    canSync,
    getModulePermissions,
    isAdmin,
    getRole,
    user,
    MODULES,
    OPERATIONS,
    ROLE_LABELS,
  };
}

/**
 * Get default permissions for a role
 */
export function getDefaultPermissionsForRole(
  role: UserRole
): Record<PermissionModule, PermissionOperation[]> {
  return DEFAULT_ROLE_PERMISSIONS[role] || {};
}
